import type { AxisScale, HeightAxis } from '../types';
import type { HorizontalAxis } from './horizontalAxes';

const toDivisions = (values: readonly string[]): number =>
  Math.max(1, values.length - 1);

/**
 * Where the first category stands on a height axis. One step above the floor, so a column for it
 * rises like any other instead of lying flat as an empty bin does.
 */
export const CATEGORY_HEIGHT_FIRST_POSITION = 1;

/**
 * An ordinal axis over a list of categories: one at each whole position from 0, a tick on each,
 * padded half a step so the end categories stand whole.
 */
export const toCategoryAxis = (values: readonly string[]): HorizontalAxis => ({
  range: [-0.5, values.length - 0.5],
  tickCount: toDivisions(values),
  padding: [0.5, 0.5],
});

/** A height axis from an unnamed floor at 0, with a tick on each category from 1 up. */
export const toCategoryHeightAxis = (
  values: readonly string[]
): HeightAxis => ({
  range: [0, values.length],
  tickCount: Math.max(1, values.length),
});

/** Categories at whole positions from 0, as on a horizontal axis. */
export const toCategoryScale = (values: readonly string[]): AxisScale => ({
  kind: 'category',
  values,
  firstPosition: 0,
});

/** Categories at whole positions from 1, as on a category height axis. */
export const toCategoryHeightScale = (
  values: readonly string[]
): AxisScale => ({
  kind: 'category',
  values,
  firstPosition: CATEGORY_HEIGHT_FIRST_POSITION,
});

/**
 * A position named by the nearest category. Ticks fall on whole positions and read exactly; an
 * interpolated, averaged or binned position between two reads as the nearer. A position outside
 * the list, such as a height axis's floor, names nothing.
 */
export const toCategoryFormat =
  (values: readonly string[], firstPosition: number) =>
  (position: number): string =>
    values[Math.round(position) - firstPosition] ?? '';
